import { useEffect } from 'react'
import { Button, EnrollmentHelpText } from '@/shared/components'
import { StepHeader } from '../StepHeader'
import type { EnglishTestResult } from './types'

type EnglishProficiencyResultProps = {
  continueError: string | null
  currentStep: number
  effectiveTestResult: EnglishTestResult | null
  isRetryLockout: boolean
  onAction: () => void
  onBack: () => void
  resultButtonLabel: string
  resultButtonLoading: boolean
  retryLockoutMessage: string | null
  testPassed: boolean
  totalSteps: number
}

export function EnglishProficiencyResult({
  continueError,
  currentStep,
  effectiveTestResult,
  isRetryLockout,
  onAction,
  onBack,
  resultButtonLabel,
  resultButtonLoading,
  retryLockoutMessage,
  testPassed,
  totalSteps
}: EnglishProficiencyResultProps) {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }, [])

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-4xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">English Proficiency Test</h1>
        </div>

        <div
          className="animate-fade-in-up mx-auto max-w-3xl rounded-2xl border-2 border-gray-lighter bg-white p-6 text-center shadow-sm"
          style={{ animationDelay: '120ms' }}
        >
          {!isRetryLockout ? (
            <>
              <h2 className="mb-1.5 text-sm font-semibold uppercase tracking-[0.5px] text-primary">
                Test Result
              </h2>
              <h3 className="mb-4 text-2xl font-bold text-primary">
                {testPassed ? 'Successful' : 'Unsuccessful'}
              </h3>
            </>
          ) : null}

          {effectiveTestResult && !isRetryLockout ? (
            <p className="mb-4 text-sm font-medium text-slate-600">
              Score: <span className="text-slate-900">{effectiveTestResult.score}</span>
              {effectiveTestResult.passingScore > 0 ? (
                <>
                  {' '}
                  | Passing score:{' '}
                  <span className="text-slate-900">{effectiveTestResult.passingScore}</span>
                </>
              ) : null}
            </p>
          ) : null}

          {testPassed ? (
            <>
              <p className="mb-4 text-[16px] leading-[1.6] text-gray-dark">
                You passed the English proficiency test and can continue to the next step of
                enrollment.
              </p>

              <p className="mx-auto mb-5 max-w-xl text-sm leading-relaxed text-[#707070]">
                Your responses show that you can understand common training instructions, safety
                guidance, and written directions used during CDL enrollment.
              </p>
            </>
          ) : isRetryLockout ? (
            <>
              <p className="mb-4 text-[16px] leading-[1.6] text-gray-dark">
                <span className="font-semibold text-primary">{retryLockoutMessage}</span>
              </p>

              <p className="mx-auto mb-5 max-w-xl text-sm leading-relaxed text-[#707070]">
                Please wait until the retry window has passed before starting the English
                proficiency test again.
              </p>
            </>
          ) : (
            <>
              <p className="mb-4 text-[16px] leading-[1.6] text-gray-dark">
                Your test score did{' '}
                <span className="font-semibold text-primary">not meet the passing requirement</span>
                .
                <br />
                You can retake the test now.
              </p>

              <p className="mx-auto mb-5 max-w-xl text-sm leading-relaxed text-[#707070]">
                Please review each question carefully before submitting your next attempt. If the
                allowed attempts are used, a waiting period may apply before another retake is
                available.
              </p>
            </>
          )}

          {continueError ? (
            <div className="mx-auto mb-4 max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700">
              {continueError}
            </div>
          ) : null}

          <div className="mx-auto max-w-md">
            <Button
              type="button"
              onClick={onAction}
              fullWidth
              loading={resultButtonLoading}
              disabled={resultButtonLoading}
              className="hover:-translate-y-0.5 active:scale-95"
            >
              {resultButtonLabel}
            </Button>
          </div>

          <div className="mt-4">
            <EnrollmentHelpText />
          </div>
        </div>
      </div>
    </div>
  )
}
